import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  Search, Filter, ChevronRight, AlertTriangle, ShieldAlert, TrendingDown,
  TrendingUp, Users, Timer, UserPlus, LogOut, ArrowUpRight, Bell,
  Activity, ClipboardList, Pill, Stethoscope, Loader2, X, Sparkles, RefreshCw,
} from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

const RISK = {
  high:   { color: "#F87171", bg: "rgba(248,113,113,0.10)", dot: "#F87171", label: "HIGH",   order: 0 },
  medium: { color: "#FBBF24", bg: "rgba(251,191,36,0.10)", dot: "#FBBF24", label: "MED",    order: 1 },
  low:    { color: "#4ADE80", bg: "rgba(74,222,128,0.10)", dot: "#4ADE80", label: "LOW",    order: 2 },
  unknown:{ color: "#94A3B8", bg: "rgba(148,163,184,0.10)", dot: "#94A3B8", label: "—",     order: 3 },
};

const SPEC_ACCENT = {
  nephrology: "#4ADE80",  cardiology: "#F87171",  neurology: "#A78BFA",
  oncology: "#FBBF24",    endocrinology: "#8B5CF6", pulmonology: "#38BDF8",
  gastroenterology: "#F97316", rheumatology: "#EC4899", infectious_disease: "#22C55E",
};

/**
 * PatientCommandCenter — dense, searchable provider dashboard designed for
 * rosters of 50-500+ patients.
 *
 * Layout:
 *  - Slim header (name, specialty, KPI strip on the right)
 *  - Filter bar (search + risk chips + sort + status)
 *  - Split view:
 *      LEFT (60%)  — Dense one-row-per-patient list with keyboard nav (↑↓ Enter)
 *      RIGHT (40%) — Preview panel for the highlighted patient (sticky)
 *
 * Design intent: any provider can scan 100 patients in under 10 seconds and
 * jump to a patient chart in one click / one key press.
 */
export default function PatientCommandCenter() {
  const { setViewingPatient, user, logout } = useAuth();
  const nav = useNavigate();
  const [patients, setPatients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [risk, setRisk] = useState("all");
  const [sortBy, setSortBy] = useState("risk");
  const [selected, setSelected] = useState(null);
  const [refInbox, setRefInbox] = useState([]);
  const [aiBrief, setAiBrief] = useState(null);
  const [aiLoading, setAiLoading] = useState(true);

  const loadBrief = useCallback(async (force = false) => {
    setAiLoading(true);
    try {
      const { data } = await api.get(`/provider/roster-summary${force ? "?force=true" : ""}`);
      setAiBrief(data);
    } catch { /* soft */ } finally { setAiLoading(false); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: pd }, { data: ad }, refResp] = await Promise.all([
        api.get("/provider/patients"),
        api.get("/provider/alerts"),
        api.get("/referrals/inbox?status=open").catch(() => ({ data: { referrals: [] } })),
      ]);
      setPatients(pd.patients || []);
      setAlerts(ad.alerts || []);
      setRefInbox(refResp?.data?.referrals || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load roster");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); loadBrief(); }, [load, loadBrief]);

  const kpis = useMemo(() => {
    const high = patients.filter((p) => p.overall_risk === "high").length;
    const med  = patients.filter((p) => p.overall_risk === "medium").length;
    const low  = patients.filter((p) => p.overall_risk === "low").length;
    // Alerts by patient
    const alertedIds = new Set(alerts.map((a) => a.patient_id));
    return { total: patients.length, high, med, low, alerted: alertedIds.size };
  }, [patients, alerts]);

  const filtered = useMemo(() => {
    let out = patients;
    if (risk !== "all") out = out.filter((p) => p.overall_risk === risk);
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter((p) =>
        (p.name || "").toLowerCase().includes(q)
        || (p.email || "").toLowerCase().includes(q)
        || (p.ckd_stage || "").toLowerCase().includes(q)
      );
    }
    if (sortBy === "risk") {
      out = [...out].sort((a, b) => (RISK[a.overall_risk]?.order ?? 4) - (RISK[b.overall_risk]?.order ?? 4));
    } else if (sortBy === "name") {
      out = [...out].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortBy === "egfr") {
      out = [...out].sort((a, b) => (a.latest_egfr ?? 999) - (b.latest_egfr ?? 999));
    } else if (sortBy === "trend") {
      out = [...out].sort((a, b) => (a.egfr_trend ?? 0) - (b.egfr_trend ?? 0));
    }
    return out;
  }, [patients, risk, query, sortBy]);

  // Keep selection valid after filter change
  useEffect(() => {
    if (!filtered.length) { setSelected(null); return; }
    if (!selected || !filtered.find((p) => p.patient_id === selected.patient_id)) {
      setSelected(filtered[0]);
    }
  }, [filtered, selected]);

  const openPatient = useCallback((p) => {
    setViewingPatient(p);
    nav("/");
  }, [setViewingPatient, nav]);

  const patientAlerts = useCallback((pid) => alerts.filter((a) => a.patient_id === pid), [alerts]);

  // Keyboard navigation (↑↓ Enter)
  useEffect(() => {
    function onKey(e) {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      const idx = filtered.findIndex((p) => p.patient_id === selected?.patient_id);
      if (e.key === "ArrowDown" && idx < filtered.length - 1) {
        setSelected(filtered[idx + 1]); e.preventDefault();
      } else if (e.key === "ArrowUp" && idx > 0) {
        setSelected(filtered[idx - 1]); e.preventDefault();
      } else if (e.key === "Enter" && selected) {
        openPatient(selected); e.preventDefault();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filtered, selected, openPatient]);

  return (
    <div className="min-h-screen bg-[#080A09] text-white" data-testid="patient-command-center">
      {/* ============ Slim header ============ */}
      <div className="border-b border-[#272E2B] sticky top-0 bg-[#080A09]/95 backdrop-blur z-20">
        <div className="max-w-[1500px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-2xl bg-[#4ADE80] flex items-center justify-center shrink-0">
              <span className="text-black font-heading font-bold">M</span>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider"
                   style={{ color: SPEC_ACCENT[user?.specialty] || "#4ADE80" }}>
                {(user?.specialty || "provider").replace("_", " ")} · Command Center
              </div>
              <div className="font-heading text-lg leading-tight truncate">{user?.name}</div>
            </div>
          </div>

          {/* KPI strip */}
          <div className="hidden md:flex items-center gap-4">
            <KPI label="Roster"   value={kpis.total} accent="#94A3B8" testid="kpi-total" />
            <KPI label="High"     value={kpis.high}  accent="#F87171" testid="kpi-high" />
            <KPI label="Medium"   value={kpis.med}   accent="#FBBF24" testid="kpi-med" />
            <KPI label="Low"      value={kpis.low}   accent="#4ADE80" testid="kpi-low" />
            <KPI label="Alerts"   value={kpis.alerted} accent="#EC4899" testid="kpi-alerts" />
            {refInbox.length > 0 && (
              <KPI label="Referrals" value={refInbox.length} accent="#8B5CF6" testid="kpi-referrals" />
            )}
          </div>

          <div className="flex items-center gap-2">
            <button onClick={() => nav("/encounters")}
                    data-testid="cc-encounters-btn"
                    className="text-xs px-3 py-1.5 rounded-full border border-[#272E2B] text-[#A3ACA7] hover:bg-[#1C201E] hover:text-white transition-colors">
              Check-ins
            </button>
            <button onClick={() => nav("/provider/add-patient")}
                    data-testid="cc-add-patient-btn"
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#4ADE80] text-black font-semibold">
              <UserPlus size={12} /> Add
            </button>
            <button onClick={async () => { await logout(); nav("/login"); }}
                    data-testid="cc-logout-btn"
                    className="text-xs p-2 rounded-full border border-[#272E2B] text-[#A3ACA7] hover:bg-[#1C201E]">
              <LogOut size={13} />
            </button>
            <ThemeToggle />
          </div>
        </div>
      </div>

      {/* ============ AI Morning Brief (structured card) ============ */}
      <div className="max-w-[1500px] mx-auto px-6 pt-4" data-testid="cc-ai-brief">
        <MorningBriefCard brief={aiBrief} loading={aiLoading} onRefresh={() => loadBrief(true)} />
      </div>

      {/* ============ Filter bar ============ */}
      <div className="max-w-[1500px] mx-auto px-6 pt-4 pb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A8480]" />
            <input value={query}
                   onChange={(e) => setQuery(e.target.value)}
                   data-testid="cc-search"
                   placeholder="Search name, email, CKD stage…"
                   className="ros-input pl-9 pr-3 py-2 w-full text-sm" />
          </div>
          <div className="flex items-center gap-1 ml-1" data-testid="cc-risk-chips">
            {[
              { key: "all",    label: "All",    accent: "#94A3B8" },
              { key: "high",   label: "High",   accent: "#F87171" },
              { key: "medium", label: "Medium", accent: "#FBBF24" },
              { key: "low",    label: "Low",    accent: "#4ADE80" },
            ].map((r) => {
              const on = risk === r.key;
              const count = r.key === "all" ? patients.length
                : patients.filter((p) => p.overall_risk === r.key).length;
              return (
                <button key={r.key} onClick={() => setRisk(r.key)}
                        data-testid={`cc-risk-${r.key}`}
                        className={`text-xs px-3 py-1.5 rounded-full font-bold transition-colors ${
                          on ? "text-black" : "bg-[#1C201E] text-[#A3ACA7] hover:text-white"
                        }`}
                        style={on ? { background: r.accent } : {}}>
                  {r.label} ({count})
                </button>
              );
            })}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-[#7A8480]">Sort</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                    data-testid="cc-sort"
                    className="ros-input text-xs py-1.5 min-w-[130px]">
              <option value="risk">Risk (high→low)</option>
              <option value="name">Name (A→Z)</option>
              <option value="egfr">eGFR (low→high)</option>
              <option value="trend">Fastest decline</option>
            </select>
          </div>
        </div>
      </div>

      {/* ============ Referral inbox notice ============ */}
      {refInbox.length > 0 && (
        <div className="max-w-[1500px] mx-auto px-6 pb-3" data-testid="cc-referral-notice">
          <div className="rounded-2xl border border-[#8B5CF6]/40 bg-[#8B5CF6]/8 p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center">
              <ArrowUpRight size={14} className="text-[#8B5CF6]" />
            </div>
            <div className="flex-1 text-sm">
              <span className="text-white font-semibold">{refInbox.length} incoming referral{refInbox.length === 1 ? "" : "s"}</span>
              <span className="text-[#A3ACA7]"> from other doctors — pending your review</span>
            </div>
            <button onClick={() => nav("/encounters")}
                    data-testid="cc-open-referrals"
                    className="text-xs px-3 py-1.5 rounded-full bg-[#8B5CF6] text-white font-semibold">
              Review →
            </button>
          </div>
        </div>
      )}

      {/* ============ Split view: list + preview ============ */}
      <div className="max-w-[1500px] mx-auto px-6 pb-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] gap-4"
           data-testid="cc-split">
        <PatientList
          patients={filtered}
          selected={selected}
          onSelect={setSelected}
          onOpen={openPatient}
          patientAlerts={patientAlerts}
          loading={loading}
          totalCount={patients.length}
        />
        <PatientPreview patient={selected}
                         onOpen={openPatient}
                         alertsForPatient={selected ? patientAlerts(selected.patient_id) : []} />
      </div>
    </div>
  );
}


function KPI({ label, value, accent, testid }) {
  return (
    <div className="text-right" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-[#7A8480]">{label}</div>
      <div className="font-heading text-lg leading-tight" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}


function PatientList({ patients, selected, onSelect, onOpen, patientAlerts, loading, totalCount }) {
  if (loading) {
    return (
      <div className="ros-card flex items-center gap-2">
        <Loader2 className="animate-spin" size={14} /> Loading roster…
      </div>
    );
  }
  if (!patients.length) {
    return (
      <div className="ros-card text-center py-16" data-testid="cc-empty">
        <Users size={36} className="mx-auto text-[#7A8480] mb-3" />
        <div className="font-heading text-lg">
          {totalCount === 0 ? "No patients on your roster yet" : "No patients match your filters"}
        </div>
        <div className="text-xs text-[#7A8480] mt-2">
          {totalCount === 0 ? "Add a patient to get started." : "Try clearing search or risk filter."}
        </div>
      </div>
    );
  }
  return (
    <div className="ros-card p-0 overflow-hidden" data-testid="cc-list">
      {/* Column header */}
      <div className="grid grid-cols-[60px_1fr_90px_90px_60px_40px] items-center gap-2 px-3 py-2 text-[9px] uppercase tracking-wider text-[#7A8480] border-b border-[#272E2B]">
        <span>Risk</span>
        <span>Patient</span>
        <span>Key metric</span>
        <span>Trend</span>
        <span>Alerts</span>
        <span></span>
      </div>
      <div className="max-h-[70vh] overflow-y-auto">
        {patients.map((p) => (
          <PatientRow key={p.patient_id}
                      p={p}
                      selected={selected?.patient_id === p.patient_id}
                      onSelect={onSelect}
                      onOpen={onOpen}
                      alertCount={patientAlerts(p.patient_id).length} />
        ))}
      </div>
      <div className="px-3 py-2 text-[10px] text-[#7A8480] border-t border-[#272E2B] bg-[#0F1312]"
           data-testid="cc-list-footer">
        {patients.length} of {totalCount} patients · use ↑↓ to navigate, Enter to open
      </div>
    </div>
  );
}


function PatientRow({ p, selected, onSelect, onOpen, alertCount }) {
  const rs = RISK[p.overall_risk] || RISK.unknown;
  const trendDown = p.egfr_trend != null && p.egfr_trend < 0;
  const initials = (p.name || "?").split(" ").slice(0, 2).map((s) => s[0]).join("").toUpperCase();

  return (
    <div onClick={() => onSelect(p)}
         onDoubleClick={() => onOpen(p)}
         data-testid={`cc-row-${p.email}`}
         className={`grid grid-cols-[60px_1fr_90px_90px_60px_40px] items-center gap-2 px-3 py-2.5 border-l-2 cursor-pointer transition-colors
                     ${selected ? "bg-[#1C201E]" : "hover:bg-[#141817]"}`}
         style={{ borderLeftColor: rs.color }}>
      {/* Risk dot */}
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full" style={{ background: rs.dot }}></span>
        <span className="text-[10px] font-bold" style={{ color: rs.color }}>{rs.label}</span>
      </div>
      {/* Name + demographics */}
      <div className="min-w-0 flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-[#1C201E] flex items-center justify-center font-bold text-[10px] shrink-0"
             style={{ color: rs.color }}>
          {initials}
        </div>
        <div className="min-w-0">
          <div className="text-sm text-white truncate">{p.name}</div>
          <div className="text-[10px] text-[#7A8480]">
            {p.age}y · {p.sex} · CKD {p.ckd_stage || "—"} · {p.medication_count || 0} meds
          </div>
        </div>
      </div>
      {/* Key metric — eGFR */}
      <div className="text-xs">
        <div className="text-white font-bold">{p.latest_egfr ?? "—"}</div>
        <div className="text-[9px] text-[#7A8480] uppercase tracking-wider">eGFR</div>
      </div>
      {/* Trend */}
      <div className="text-xs flex items-center gap-1"
           style={{ color: trendDown ? "#F87171" : (p.egfr_trend > 0 ? "#4ADE80" : "#7A8480") }}>
        {trendDown ? <TrendingDown size={12} /> : (p.egfr_trend > 0 ? <TrendingUp size={12} /> : null)}
        <span>{p.egfr_trend != null ? `${p.egfr_trend > 0 ? "+" : ""}${p.egfr_trend}` : "—"}</span>
      </div>
      {/* Alerts */}
      <div className="text-xs flex items-center gap-1">
        {alertCount > 0 ? (
          <span className="flex items-center gap-1 text-[#EC4899]"
                data-testid={`cc-row-alerts-${p.email}`}>
            <Bell size={11} /> {alertCount}
          </span>
        ) : (
          <span className="text-[#7A8480]">—</span>
        )}
      </div>
      {/* Open */}
      <button onClick={(e) => { e.stopPropagation(); onOpen(p); }}
              data-testid={`cc-row-open-${p.email}`}
              className="text-[#A3ACA7] hover:text-[#4ADE80] p-1">
        <ChevronRight size={14} />
      </button>
    </div>
  );
}


function PatientPreview({ patient, onOpen, alertsForPatient }) {
  if (!patient) {
    return (
      <div className="ros-card text-center py-16 text-xs text-[#7A8480]"
           data-testid="cc-preview-empty">
        <ClipboardList size={32} className="mx-auto mb-2 opacity-50" />
        Select a patient to preview their chart here.
      </div>
    );
  }
  const p = patient;
  const rs = RISK[p.overall_risk] || RISK.unknown;
  const specKeys = Object.keys(p.specialty_metrics || {});

  return (
    <div className="ros-card space-y-4 sticky top-24 self-start max-h-[80vh] overflow-y-auto"
         style={{ borderColor: `${rs.color}60` }}
         data-testid="cc-preview">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ color: rs.color, background: rs.bg }}>{rs.label}</span>
            <div className="font-heading text-xl leading-tight truncate">{p.name}</div>
          </div>
          <div className="text-[10px] text-[#7A8480] mt-1">
            {p.age}y · {p.sex} · CKD {p.ckd_stage || "—"} · {p.email}
          </div>
        </div>
      </div>

      {/* Vitals mini grid */}
      <div className="grid grid-cols-3 gap-2" data-testid="cc-preview-vitals">
        <MiniStat label="eGFR" value={p.latest_egfr} accent="#4ADE80" />
        <MiniStat label="ACR"  value={p.latest_acr}  accent="#FBBF24" unit="mg/g" />
        <MiniStat label="KFRE 5y" value={p.kfre_5yr != null ? `${p.kfre_5yr}%` : "—"} accent="#F87171" />
      </div>

      {/* Specialty snapshots */}
      {specKeys.length > 0 && (
        <div className="space-y-1" data-testid="cc-preview-specialties">
          <div className="text-[10px] uppercase tracking-wider text-[#7A8480]">
            Cross-specialty snapshots
          </div>
          <div className="space-y-1.5">
            {specKeys.map((k) => {
              const sm = p.specialty_metrics[k];
              if (!sm) return null;
              const accent = SPEC_ACCENT[k] || "#94A3B8";
              return (
                <div key={k} className="rounded-lg bg-[#141817] px-2.5 py-1.5"
                     data-testid={`cc-preview-spec-${k}`}>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="uppercase tracking-wider" style={{ color: accent }}>
                      {k.replace("_", " ")}
                    </span>
                    <span className="text-[#7A8480]">{sm.status || ""}</span>
                  </div>
                  <div className="text-xs text-white leading-tight mt-0.5">
                    {k === "cardiology" && sm.systolic && (<>BP {sm.systolic}/{sm.diastolic}</>)}
                    {k === "neurology" && (<>{sm.primary_metric}: {sm.value} · {sm.secondary}</>)}
                    {k === "oncology" && (<>{sm.cancer_type} · stage {sm.stage} · {sm.tnm}</>)}
                    {!["cardiology", "neurology", "oncology"].includes(k) && (
                      <span className="text-[#A3ACA7]">
                        {sm.value ?? sm.label ?? "—"}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Alerts */}
      {alertsForPatient.length > 0 && (
        <div className="space-y-1" data-testid="cc-preview-alerts">
          <div className="text-[10px] uppercase tracking-wider text-[#EC4899]">
            Active alerts ({alertsForPatient.length})
          </div>
          <ul className="space-y-1">
            {alertsForPatient.slice(0, 3).map((a) => (
              <li key={a.id} className="text-xs text-white flex items-start gap-2">
                <AlertTriangle size={11} className="text-[#EC4899] mt-0.5 shrink-0" />
                <span className="leading-tight">{a.insight?.title || a.title || "Alert"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Risk flags */}
      {p.risk_flags?.length > 0 && (
        <div className="space-y-1" data-testid="cc-preview-risks">
          <div className="text-[10px] uppercase tracking-wider text-[#7A8480]">Risk flags</div>
          <div className="flex flex-wrap gap-1">
            {p.risk_flags.map((f, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded-md flex items-center gap-1"
                    style={{
                      color: RISK[f.level]?.color || "#A3ACA7",
                      background: RISK[f.level]?.bg || "rgba(148,163,184,0.10)",
                      border: `1px solid ${RISK[f.level]?.color || "#94A3B8"}33`,
                    }}>
                {f.level === "high" ? <ShieldAlert size={9} /> : null}
                {f.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <button onClick={() => onOpen(p)}
              data-testid="cc-preview-open"
              className="w-full flex items-center justify-center gap-1.5 text-sm px-4 py-2.5 rounded-full bg-[#4ADE80] text-black font-semibold hover:brightness-110">
        Open chart <ChevronRight size={14} />
      </button>
    </div>
  );
}


function MiniStat({ label, value, accent, unit }) {
  return (
    <div className="rounded-lg bg-[#141817] px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-[#7A8480]">{label}</div>
      <div className="font-heading text-base leading-tight" style={{ color: accent }}>
        {value ?? "—"}{unit && value != null ? <span className="text-[9px] text-[#7A8480] ml-0.5">{unit}</span> : null}
      </div>
    </div>
  );
}


const STATUS_STYLES = {
  quiet:     { accent: "#4ADE80", label: "All clear",    bg: "rgba(74,222,128,0.06)",  border: "rgba(74,222,128,0.30)" },
  attention: { accent: "#FBBF24", label: "Attention",    bg: "rgba(251,191,36,0.06)",  border: "rgba(251,191,36,0.30)" },
  urgent:    { accent: "#F87171", label: "Urgent",       bg: "rgba(248,113,113,0.08)", border: "rgba(248,113,113,0.35)" },
};

const PRIO_STYLES = {
  high: { color: "#F87171", bg: "rgba(248,113,113,0.14)", label: "HIGH" },
  med:  { color: "#FBBF24", bg: "rgba(251,191,36,0.14)",  label: "MED" },
  low:  { color: "#4ADE80", bg: "rgba(74,222,128,0.14)",  label: "LOW" },
};


function MorningBriefCard({ brief, loading, onRefresh }) {
  const s = brief?.structured;
  const status = STATUS_STYLES[s?.status] || STATUS_STYLES.attention;
  const tiles = s?.tiles || [];
  const actions = s?.actions || [];
  const closer = s?.closer;

  return (
    <div className="rounded-2xl border p-5 space-y-4"
         style={{ background: status.bg, borderColor: status.border }}
         data-testid="cc-ai-brief-card">
      {/* Header row */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
             style={{ background: `${status.accent}22`, border: `1px solid ${status.accent}55` }}>
          <Sparkles size={16} style={{ color: status.accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider" style={{ color: status.accent }}>
              Morning Brief
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: `${status.accent}22`, color: status.accent }}>
              {status.label}
            </span>
            {brief?.cached && (
              <span className="text-[9px] text-[#7A8480] uppercase tracking-wider">· cached</span>
            )}
          </div>
          <div className="text-base text-white leading-snug mt-1.5"
               data-testid="cc-ai-brief-text">
            {loading
              ? <span className="text-[#7A8480]">Composing today&rsquo;s brief…</span>
              : (s?.headline || "No brief available.")}
          </div>
        </div>
        <button onClick={onRefresh}
                data-testid="cc-ai-brief-refresh"
                disabled={loading}
                className="text-[10px] px-2.5 py-1.5 rounded-full border transition-colors flex items-center gap-1 disabled:opacity-50 shrink-0"
                style={{ borderColor: `${status.accent}55`, color: status.accent }}>
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          {loading ? "Refreshing" : "Refresh"}
        </button>
      </div>

      {!loading && s && (
        <>
          {/* Tile row */}
          <div className="grid grid-cols-3 gap-2" data-testid="cc-brief-tiles">
            {tiles.map((t, i) => (
              <div key={i} className="rounded-xl bg-[#0F1312]/70 border border-[#272E2B] p-3">
                <div className="text-[9px] uppercase tracking-wider text-[#7A8480]">{t.label}</div>
                <div className="font-heading text-2xl leading-tight" style={{ color: status.accent }}>
                  {t.value}
                </div>
                <div className="text-[10px] text-[#A3ACA7] leading-tight mt-0.5 line-clamp-2">
                  {t.hint}
                </div>
              </div>
            ))}
          </div>

          {/* Actions list */}
          {actions.length > 0 && (
            <div className="space-y-1.5" data-testid="cc-brief-actions">
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
                Suggested actions
              </div>
              {actions.map((a, i) => {
                const p = PRIO_STYLES[a.priority] || PRIO_STYLES.med;
                return (
                  <div key={i} className="flex items-start gap-2 text-sm"
                       data-testid={`cc-brief-action-${i}`}>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded mt-0.5 shrink-0"
                          style={{ background: p.bg, color: p.color }}>{p.label}</span>
                    <div className="flex-1 min-w-0">
                      {a.patient && (
                        <span className="text-white font-semibold mr-1.5">{a.patient}:</span>
                      )}
                      <span className="text-[#A3ACA7]">{a.task}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {closer && (
            <div className="text-xs text-[#7A8480] italic pt-2 border-t border-[#272E2B]/60"
                 data-testid="cc-brief-closer">
              {closer}
            </div>
          )}
        </>
      )}
    </div>
  );
}
